const CACHE_SECTIONS = {
  video: {
    keys: ['lastVideoUrl', 'lastVideoAnalysisDate', 'cachedAnalysisData'],
    timestampKey: 'lastVideoAnalysisDate',
    maxHours: 7 * 24
  },
  
  channel: {
    keys: ['lastChannelUrl', 'lastChannelAnalysisDate', 'cachedChannelData'],
    timestampKey: 'lastChannelAnalysisDate', 
    maxHours: 7 * 24
  },
  
  channelCategories: {
    keys: [],
    timestampKey: null,
    maxHours: 3 * 24,
    isLanguageDependent: true
  },
  
  videoDiscoveryCategories: {
    keys: ['videoCategoriesCache', 'videoCategoriesCacheTime'],
    timestampKey: 'videoCategoriesCacheTime',
    maxHours: 3 * 24
  },

  videoDiscoveryCache: {
    keys: ['videoDiscoveryCache', 'videoDiscoveryFilters', 'videoDiscoveryCacheTime'],
    timestampKey: 'videoDiscoveryCacheTime',
    maxHours: 2
  },

  channelDiscoveryCache: {
    keys: ['channelDiscoveryCache', 'channelDiscoveryFilters', 'channelDiscoveryCacheTime'],
    timestampKey: 'channelDiscoveryCacheTime',
    maxHours: 4
  },

  researchDashboardCache: {
    keys: ['researchDashboardCache', 'researchDashboardFilters', 'researchDashboardCacheTime'],
    timestampKey: 'researchDashboardCacheTime',
    maxHours: 2
  },

  researchMostViewedCache: {
    keys: ['researchMostViewedCache', 'researchMostViewedFilters', 'researchMostViewedCacheTime'],
    timestampKey: 'researchMostViewedCacheTime',
    maxHours: 2
  },

  researchKeywordsCache: {
    keys: ['researchKeywordsCache', 'researchKeywordsFilters', 'researchKeywordsCacheTime'],
    timestampKey: 'researchKeywordsCacheTime',
    maxHours: 1
  },

  researchMonetizationCache: {
    keys: ['researchMonetizationCache', 'researchMonetizationFilters', 'researchMonetizationCacheTime'],
    timestampKey: 'researchMonetizationCacheTime',
    maxHours: 2
  }
};

let lastCleanupCheck = 0;
let lastFocusCleanup = 0;
const MIN_CLEANUP_INTERVAL = 2 * 60 * 60 * 1000;
const MIN_FOCUS_CLEANUP_INTERVAL = 30 * 60 * 1000;

const getStoredLanguages = () => {
  const languages = new Set(['ru']);
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.includes('channelCategoriesCache_')) {
      const lang = key.replace('channelCategoriesCache_', '');
      if (lang && lang.length >= 2) {
        languages.add(lang);
      }
    }
  }
  
  return Array.from(languages);
};

const getLanguageDependentKeys = (sectionName) => {
  if (sectionName !== 'channelCategories') return [];
  
  const languages = getStoredLanguages();
  const keys = [];
  
  languages.forEach(lang => {
    keys.push(`channelCategoriesCache_${lang}`);
    keys.push(`channelCategoriesCacheTime_${lang}`);
  });
  
  return keys;
};

const isSectionExpired = (sectionName) => {
  const section = CACHE_SECTIONS[sectionName];
  if (!section) return false;
  
  if (section.isLanguageDependent) {
    const languages = getStoredLanguages();
    let hasExpiredCache = false;
    
    languages.forEach(lang => {
      const timestampKey = `channelCategoriesCacheTime_${lang}`;
      try {
        const timestamp = localStorage.getItem(timestampKey);
        if (timestamp) {
          const hours = (new Date() - new Date(parseInt(timestamp))) / (1000 * 60 * 60);
          if (hours > section.maxHours) {
            hasExpiredCache = true;
          }
        }
      } catch (e) {
        hasExpiredCache = true;
      }
    });
    
    return hasExpiredCache;
  }
  
  if (!section.timestampKey) return false;
  
  try {
    const timestamp = localStorage.getItem(section.timestampKey);
    if (!timestamp) return false;
    
    const hours = (new Date() - new Date(parseInt(timestamp))) / (1000 * 60 * 60);
    return hours > section.maxHours;
  } catch (e) {
    return true;
  }
};

const clearSection = (sectionName) => {
  const section = CACHE_SECTIONS[sectionName];
  if (!section) return false;
  
  let cleared = false;
  let keysToClean = [];
  
  if (section.isLanguageDependent) {
    keysToClean = getLanguageDependentKeys(sectionName);
  } else {
    keysToClean = section.keys;
  }
  
  keysToClean.forEach(key => {
    if (localStorage.getItem(key)) {
      if (section.isLanguageDependent && key.includes('channelCategoriesCacheTime_')) {
        const lang = key.replace('channelCategoriesCacheTime_', '');
        try {
          const timestamp = localStorage.getItem(key);
          if (timestamp) {
            const hours = (new Date() - new Date(parseInt(timestamp))) / (1000 * 60 * 60);
            if (hours > section.maxHours) {
              localStorage.removeItem(key);
              localStorage.removeItem(`channelCategoriesCache_${lang}`);
              cleared = true;
            }
          }
        } catch (e) {
          localStorage.removeItem(key);
          localStorage.removeItem(`channelCategoriesCache_${lang}`);
          cleared = true;
        }
      } else if (!section.isLanguageDependent) {
        localStorage.removeItem(key);
        cleared = true;
      }
    }
  });
  
  return cleared;
};

const performSpecialCacheCleanup = () => {
  let cleared = 0;

  try {
    const cacheTime = localStorage.getItem('videoDiscoveryCacheTime');
    if (cacheTime) {
      const hours = (new Date() - new Date(parseInt(cacheTime))) / (1000 * 60 * 60);
      if (hours > 2) {
        localStorage.removeItem('videoDiscoveryCache');
        localStorage.removeItem('videoDiscoveryFilters');
        localStorage.removeItem('videoDiscoveryCacheTime');
        cleared++;
      }
    }
  } catch (e) {
    localStorage.removeItem('videoDiscoveryCache');
    localStorage.removeItem('videoDiscoveryFilters');
    localStorage.removeItem('videoDiscoveryCacheTime');
    cleared++;
  }

  try {
    const cacheTime = localStorage.getItem('channelDiscoveryCacheTime');
    if (cacheTime) {
      const hours = (new Date() - new Date(parseInt(cacheTime))) / (1000 * 60 * 60);
      if (hours > 4) {
        localStorage.removeItem('channelDiscoveryCache');
        localStorage.removeItem('channelDiscoveryFilters');
        localStorage.removeItem('channelDiscoveryCacheTime');
        cleared++;
      }
    }
  } catch (e) {
    localStorage.removeItem('channelDiscoveryCache');
    localStorage.removeItem('channelDiscoveryFilters');
    localStorage.removeItem('channelDiscoveryCacheTime');
    cleared++;
  }

  try {
    const cacheTime = localStorage.getItem('researchDashboardCacheTime');
    if (cacheTime) {
      const hours = (new Date() - new Date(parseInt(cacheTime))) / (1000 * 60 * 60);
      if (hours > 2) {
        localStorage.removeItem('researchDashboardCache');
        localStorage.removeItem('researchDashboardFilters');
        localStorage.removeItem('researchDashboardCacheTime');
        cleared++;
      }
    }
  } catch (e) {
    localStorage.removeItem('researchDashboardCache');
    localStorage.removeItem('researchDashboardFilters');
    localStorage.removeItem('researchDashboardCacheTime');
    cleared++;
  }

  try {
    const cacheTime = localStorage.getItem('researchMostViewedCacheTime');
    if (cacheTime) {
      const hours = (new Date() - new Date(parseInt(cacheTime))) / (1000 * 60 * 60);
      if (hours > 2) {
        localStorage.removeItem('researchMostViewedCache');
        localStorage.removeItem('researchMostViewedFilters');
        localStorage.removeItem('researchMostViewedCacheTime');
        cleared++;
      }
    }
  } catch (e) {
    localStorage.removeItem('researchMostViewedCache');
    localStorage.removeItem('researchMostViewedFilters');
    localStorage.removeItem('researchMostViewedCacheTime');
    cleared++;
  }

  try {
    const cacheTime = localStorage.getItem('researchKeywordsCacheTime');
    if (cacheTime) {
      const hours = (new Date() - new Date(parseInt(cacheTime))) / (1000 * 60 * 60);
      if (hours > 1) {
        localStorage.removeItem('researchKeywordsCache');
        localStorage.removeItem('researchKeywordsFilters');
        localStorage.removeItem('researchKeywordsCacheTime');
        cleared++;
      }
    }
  } catch (e) {
    localStorage.removeItem('researchKeywordsCache');
    localStorage.removeItem('researchKeywordsFilters');
    localStorage.removeItem('researchKeywordsCacheTime');
    cleared++;
  }

  try {
    const cacheTime = localStorage.getItem('researchMonetizationCacheTime');
    if (cacheTime) {
      const hours = (new Date() - new Date(parseInt(cacheTime))) / (1000 * 60 * 60);
      if (hours > 2) {
        localStorage.removeItem('researchMonetizationCache');
        localStorage.removeItem('researchMonetizationFilters');
        localStorage.removeItem('researchMonetizationCacheTime');
        cleared++;
      }
    }
  } catch (e) {
    localStorage.removeItem('researchMonetizationCache');
    localStorage.removeItem('researchMonetizationFilters');
    localStorage.removeItem('researchMonetizationCacheTime');
    cleared++;
  }

  return cleared;
};

export const performCacheCleanup = (force = false) => {
  const now = Date.now();
  
  if (!force && (now - lastCleanupCheck < MIN_CLEANUP_INTERVAL)) {
    return 0;
  }
  
  lastCleanupCheck = now;
  let totalCleared = 0;
  
  try {
    totalCleared += performSpecialCacheCleanup();

    Object.keys(CACHE_SECTIONS).forEach(sectionName => {
      const specialSections = [
        'videoDiscoveryCache', 'channelDiscoveryCache', 
        'researchDashboardCache', 'researchMostViewedCache', 
        'researchKeywordsCache', 'researchMonetizationCache'
      ];
      
      if (!specialSections.includes(sectionName) && isSectionExpired(sectionName)) {
        const wasCleared = clearSection(sectionName);
        if (wasCleared) totalCleared++;
      }
    });
  } catch (e) {
    // Silent error handling for production
  }
  
  return totalCleared;
};

export const startCacheCleanup = () => {
  performCacheCleanup(true);
  
  const interval = setInterval(() => {
    performCacheCleanup();
  }, 15 * 60 * 1000);
  
  const handleFocus = () => {
    const now = Date.now();
    if (now - lastFocusCleanup > MIN_FOCUS_CLEANUP_INTERVAL) {
      lastFocusCleanup = now;
      performCacheCleanup();
    }
  };
  
  window.addEventListener('focus', handleFocus);
  
  return () => {
    clearInterval(interval);
    window.removeEventListener('focus', handleFocus);
  };
};

export const showCacheStatus = () => {
  const status = Object.keys(CACHE_SECTIONS).map(sectionName => {
    const section = CACHE_SECTIONS[sectionName];
    const isExpired = isSectionExpired(sectionName);
    
    if (section.isLanguageDependent) {
      const languages = getStoredLanguages();
      return languages.map(lang => {
        const cacheKey = `channelCategoriesCache_${lang}`;
        const hasData = localStorage.getItem(cacheKey);
        return {
          section: `${sectionName} (${lang})`,
          hasData: !!hasData,
          isExpired
        };
      });
    } else {
      const hasData = section.keys.some(key => localStorage.getItem(key));
      return {
        section: sectionName,
        hasData,
        isExpired
      };
    }
  }).flat();

  const specialCaches = [
    { name: 'videoDiscovery', maxAgeHours: 2 },
    { name: 'channelDiscovery', maxAgeHours: 4 },
    { name: 'researchDashboard', maxAgeHours: 2 },
    { name: 'researchMostViewed', maxAgeHours: 2 },
    { name: 'researchKeywords', maxAgeHours: 1 },
    { name: 'researchMonetization', maxAgeHours: 2 }
  ];

  specialCaches.forEach(({ name, maxAgeHours }) => {
    const cacheExists = localStorage.getItem(`${name}Cache`);
    const cacheTime = localStorage.getItem(`${name}CacheTime`);
    let cacheExpired = false;
    let ageInfo = null;
    
    if (cacheTime) {
      const timestamp = parseInt(cacheTime);
      const hours = (new Date() - new Date(timestamp)) / (1000 * 60 * 60);
      cacheExpired = hours > maxAgeHours;
      ageInfo = { ageHours: Math.round(hours), maxAgeHours };
    }

    status.push({
      section: `${name}Cache (detailed)`,
      hasData: !!cacheExists,
      isExpired: cacheExpired,
      ...ageInfo
    });
  });

  return status;
};

export const forceClearAllCache = () => {
  let totalCleared = 0;
  
  const specialCacheKeys = [
    'videoDiscoveryCache', 'videoDiscoveryFilters', 'videoDiscoveryCacheTime',
    'channelDiscoveryCache', 'channelDiscoveryFilters', 'channelDiscoveryCacheTime',
    'researchDashboardCache', 'researchDashboardFilters', 'researchDashboardCacheTime',
    'researchMostViewedCache', 'researchMostViewedFilters', 'researchMostViewedCacheTime',
    'researchKeywordsCache', 'researchKeywordsFilters', 'researchKeywordsCacheTime',
    'researchMonetizationCache', 'researchMonetizationFilters', 'researchMonetizationCacheTime'
  ];
  
  specialCacheKeys.forEach(key => {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
      totalCleared++;
    }
  });

  Object.keys(CACHE_SECTIONS).forEach(sectionName => {
    const specialSections = [
      'videoDiscoveryCache', 'channelDiscoveryCache', 
      'researchDashboardCache', 'researchMostViewedCache', 
      'researchKeywordsCache', 'researchMonetizationCache'
    ];
    if (!specialSections.includes(sectionName)) {
      const wasCleared = clearSection(sectionName);
      if (wasCleared) totalCleared++;
    }
  });
  
  return totalCleared;
};

export const clearVideoDiscoveryCache = () => {
  try {
    const keys = ['videoDiscoveryCache', 'videoDiscoveryFilters', 'videoDiscoveryCacheTime'];
    keys.forEach(key => localStorage.removeItem(key));
    return true;
  } catch (e) {
    return false;
  }
};

export const clearChannelDiscoveryCache = () => {
  try {
    const keys = ['channelDiscoveryCache', 'channelDiscoveryFilters', 'channelDiscoveryCacheTime'];
    keys.forEach(key => localStorage.removeItem(key));
    return true;
  } catch (e) {
    return false;
  }
};

export const clearResearchDashboardCache = () => {
  try {
    const keys = ['researchDashboardCache', 'researchDashboardFilters', 'researchDashboardCacheTime'];
    keys.forEach(key => localStorage.removeItem(key));
    return true;
  } catch (e) {
    return false;
  }
};

export const clearResearchMostViewedCache = () => {
  try {
    const keys = ['researchMostViewedCache', 'researchMostViewedFilters', 'researchMostViewedCacheTime'];
    keys.forEach(key => localStorage.removeItem(key));
    return true;
  } catch (e) {
    return false;
  }
};

export const clearResearchKeywordsCache = () => {
  try {
    const keys = ['researchKeywordsCache', 'researchKeywordsFilters', 'researchKeywordsCacheTime'];
    keys.forEach(key => localStorage.removeItem(key));
    return true;
  } catch (e) {
    return false;
  }
};

export const clearResearchMonetizationCache = () => {
  try {
    const keys = ['researchMonetizationCache', 'researchMonetizationFilters', 'researchMonetizationCacheTime'];
    keys.forEach(key => localStorage.removeItem(key));
    return true;
  } catch (e) {
    return false;
  }
};

export const clearAllResearchCaches = () => {
  let totalCleared = 0;
  
  if (clearResearchDashboardCache()) totalCleared++;
  if (clearResearchMostViewedCache()) totalCleared++;
  if (clearResearchKeywordsCache()) totalCleared++;
  if (clearResearchMonetizationCache()) totalCleared++;
  
  return totalCleared;
};

export const getVideoDiscoveryCacheInfo = () => {
  try {
    const cache = localStorage.getItem('videoDiscoveryCache');
    const cacheTime = localStorage.getItem('videoDiscoveryCacheTime');
    const filters = localStorage.getItem('videoDiscoveryFilters');
    
    if (!cache || !cacheTime) {
      return { exists: false };
    }

    const cacheData = JSON.parse(cache);
    const ageHours = Math.round((new Date() - new Date(parseInt(cacheTime))) / (1000 * 60 * 60));
    const isExpired = ageHours > 2;

    return {
      exists: true,
      videosCount: cacheData.videos ? cacheData.videos.length : 0,
      ageHours,
      isExpired,
      filters: filters ? JSON.parse(filters) : null,
      timestamp: new Date(parseInt(cacheTime)).toLocaleString()
    };
  } catch (e) {
    return { exists: false, error: true };
  }
};

export const getChannelDiscoveryCacheInfo = () => {
  try {
    const cache = localStorage.getItem('channelDiscoveryCache');
    const cacheTime = localStorage.getItem('channelDiscoveryCacheTime');
    const filters = localStorage.getItem('channelDiscoveryFilters');
    
    if (!cache || !cacheTime) {
      return { exists: false };
    }

    const cacheData = JSON.parse(cache);
    const ageHours = Math.round((new Date() - new Date(parseInt(cacheTime))) / (1000 * 60 * 60));
    const isExpired = ageHours > 4;

    return {
      exists: true,
      channelsCount: cacheData.channels ? cacheData.channels.length : 0,
      ageHours,
      isExpired,
      filters: filters ? JSON.parse(filters) : null,
      timestamp: new Date(parseInt(cacheTime)).toLocaleString()
    };
  } catch (e) {
    return { exists: false, error: true };
  }
};

export const getResearchCacheInfo = () => {
  const sections = ['Dashboard', 'MostViewed', 'Keywords', 'Monetization'];
  const result = {};

  sections.forEach(section => {
    try {
      const cache = localStorage.getItem(`research${section}Cache`);
      const cacheTime = localStorage.getItem(`research${section}CacheTime`);
      const filters = localStorage.getItem(`research${section}Filters`);
      
      if (!cache || !cacheTime) {
        result[section.toLowerCase()] = { exists: false };
        return;
      }

      const cacheData = JSON.parse(cache);
      const ageHours = Math.round((new Date() - new Date(parseInt(cacheTime))) / (1000 * 60 * 60));
      
      let maxAge;
      switch (section) {
        case 'Dashboard': maxAge = 2; break;
        case 'MostViewed': maxAge = 2; break;
        case 'Keywords': maxAge = 1; break;
        case 'Monetization': maxAge = 2; break;
        default: maxAge = 2;
      }
      
      const isExpired = ageHours > maxAge;

      result[section.toLowerCase()] = {
        exists: true,
        dataCount: cacheData.data ? (
          cacheData.data.videos?.length || 
          cacheData.data.channels?.length || 
          cacheData.data.keywords?.length || 
          Object.keys(cacheData.data).length
        ) : 0,
        ageHours,
        isExpired,
        filters: filters ? JSON.parse(filters) : null,
        timestamp: new Date(parseInt(cacheTime)).toLocaleString()
      };
    } catch (e) {
      result[section.toLowerCase()] = { exists: false, error: true };
    }
  });

  return result;
};